export default function UsersService($http, appSettings, $q, initialData, gettextCatalog) {
  'ngInject';
  const userInfoBaseUrl = `${appSettings.IcebergApiUserInfoEndpoint}`;
  const datapointsBaseUrl = `${appSettings.IcebergApiDatapointsEndpoint}`;

  const flatPermissions = (user) => {
    if (user) {
      user.permissions = (_currentUser.features || []).map((u) => u.path);
    }
    return user;
  };
  let _currentUser = initialData.currentUser;

  this.currentUser = () => flatPermissions(_currentUser);

  this.currentUserAsync = () => new Promise((resolve) => resolve(flatPermissions(_currentUser)));

  this.getCurrentUser = () => {
    const def = $q.defer();

    $http.get(`${appSettings.InReachApiEndpoint}/users/me`).then(
      (response) => {
        _currentUser = response.data;
        return def.resolve(response);
      },
      () => def.reject(gettextCatalog.getString('failed to get current user')),
    );
    return def.promise;
  };

  this.hasRequiredFeatures = (features) => {
    let userFeatures = _currentUser ? _currentUser.features : [];

    for (let i = 0; i < features.length; i++) {
      let s = features[i];

      let hasState = false;

      if (userFeatures) {
        for (let j = 0; j < userFeatures.length; j++) {
          if (userFeatures[j].path === s) {
            hasState = true;
            break;
          }
        }
      }
      if (!hasState) {
        return false;
      }
    }
    return true;
  };

  this.getUsers = () => $http.get(`${userInfoBaseUrl}/users`);

  this.getUserClaims = () => $http.get(`${userInfoBaseUrl}/users/me/claims`);

  this.updateUserClaims = (claims) => {
    // No nulls for prefix. That breaks stuff.
    claims.prefix = claims.prefix || '';

    // Hack for now until we deprecate firstName, lastName claims.
    if (claims.firstName) {
      claims.given_name = claims.firstName;
    } else {
      delete claims.given_name;
    }
    if (claims.lastName) {
      claims.family_name = claims.lastName;
    } else {
      delete claims.family_name;
    }
    if (claims.firstName || claims.lastName) {
      claims.name = (
        (claims.given_name ? claims.given_name : '') +
        ' ' +
        (claims.family_name ? claims.family_name : '')
      ).trim();
    } else {
      delete claims.name;
    }

    return $http.put(`${userInfoBaseUrl}/users/me/claims`, claims);
  };

  this.patchCurrentUserClaims = (changesets) =>
    $http.patch(`${userInfoBaseUrl}/users/me/claims`, changesets);

  this.changeEmail = (newemail) => $http.post(`${userInfoBaseUrl}/users/me/email`, newemail);

  this.getUser = function (id) {
    let deferred = $q.defer();

    if (!id) {
      deferred.reject(gettextCatalog.getString('No user specified.'));
    }

    $http.get(`${userInfoBaseUrl}/users/${id}`).then(
      function (response) {
        deferred.resolve(response);
      },
      function (error) {
        deferred.reject(error);
      },
    );

    return deferred.promise;
  };

  this.patchUserInfo = (id, changesets) =>
    $http.patch(`${userInfoBaseUrl}/users/${id}/claims`, changesets);

  this.updateUserInfo = (id, firstName, lastName) => {
    if (!id) {
      return id;
    }
    const userId = id;
    const userInfo = [
      {
        op: 'update',
        claims: {
          firstName: firstName || '',
          lastName: lastName || '',
          // IANA JWT claims.
          given_name: firstName || '',
          family_name: lastName || '',
        },
      },
    ];

    return $http.patch(`${userInfoBaseUrl}/users/${userId}/claims`, userInfo);
  };

  this.postUserInfo = (id, firstName, lastName) => {
    if (!id) {
      return id;
    }
    const userId = id;
    const userInfo = [
      {
        op: 'add',
        claims: {
          firstName: firstName || '',
          lastName: lastName || '',
          // IANA JWT claims.
          given_name: firstName || '',
          family_name: lastName || '',
        },
      },
    ];

    return $http.patch(`${userInfoBaseUrl}/users/${userId}/claims`, userInfo);
  };

  this.removeUser = (id) => {
    const deferred = $q.defer();

    if (!id) {
      deferred.reject(gettextCatalog.getString('No user specified.'));
    }

    $http.delete(`${userInfoBaseUrl}/users/${id}`).then(
      function (response) {
        deferred.resolve(response);
      },
      function (error) {
        deferred.reject(error);
      },
    );

    return deferred.promise;
  };

  this.changePassword = (model) => $http.post(`${userInfoBaseUrl}/users/me/password`, model);

  this.getRoleDetails = (id) => $http.get(`${userInfoBaseUrl}/roles/${id}`);

  this.deleteRoleDetails = (id) => $http.delete(`${userInfoBaseUrl}/roles/${id}`);

  this.putRoleDetails = (data) => {
    if (!data || !data.id) return; //Invalid role details
    return $http.put(`${userInfoBaseUrl}/roles/${data.id}`, data);
  };

  this.getRolePolicies = () => $http.get(`${userInfoBaseUrl}/policies`);

  this.getDatapoints = () =>
    $http.get(`${datapointsBaseUrl}/datapoints`).then((response) => {
      response.data.forEach((datapoint) => {
        datapoint.en_description = datapoint.description;
        datapoint.description = gettextCatalog.getString(datapoint.description);
      });
      return response;
    });

  this.getDataPointsByRoleIds = (ids) =>
    $http.get(`${datapointsBaseUrl}/datapoints`, {
      params: {
        role: JSON.stringify(ids),
      },
    });

  this.patchUserRoles = (id, changes) => {
    if (!id) {
      return id;
    }

    return $http.patch(`${userInfoBaseUrl}/users/${id}/roles`, changes);
  };

  this.isUserAnAdministrator = () => {
    let isAdmin = false;

    this.currentUser().roles.data.forEach((role) => {
      if (role.name === 'Administrator') {
        isAdmin = true;
      }
    });

    return isAdmin;
  };

  /*
    This role ID comparison is for Sunbelt UK Expiry feature, it is not to be used for other accounts and roles unless approved
  */
  this.canUserEditExpiry = () => {
    const user = this.currentUser();
    const approvedRoleIds = [-1, 1685525393803260437, 1642277373393525597];
    
    for (let i = 0; i < user.roles.data.length; ++i) {
      if (
        this.hasRequiredFeatures(['access.operator_expiry']) &&
        approvedRoleIds.includes(user.roles.data[i].id)
      ) {
        return true;
      }
    }

    return false;
  };
}
